import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { copyListItem, deleteListItem } from "./Api/CurationApi";
import { isAuthenticated } from "./Api/services/auth/authClient";
import { useAuth0Logout } from "./Auth0/useAuth0Logout";
import { CurationItem, PathItem } from "./Api/types";
import AddContentModal from "./Components/AddContentModal";
import { EditContentModal } from "./Components/EditContentModal";
import ContentManagerBreadcrumbs from "./Components/ContentManagerBreadcrumbs";
import ContentChildrenGrid from "./Components/ContentChildrenGrid";
import useContentManagerData from "./hooks/useContentManagerData";
import "../styles/common.css";
import "./styling/ContentManager.css";
import "./styling/ToggleSwitch.css";
import "./Components/styling/AddContentModal.css";

const BaseActionIcon: React.FC<{ className?: string; children: React.ReactNode }> = ({
    className,
    children,
}) => (
    <svg
        className={className}
        xmlns="http://www.w3.org/2000/svg"
        height="24px"
        viewBox="0 0 24 24"
        width="24px"
        fill="currentColor"
    >
        <path d="M0 0h24v24H0V0z" fill="none" />
        {children}
    </svg>
);

const ContentManager: React.FC = () => {
    const { listId } = useParams<{ listId: string }>();
    const navigate = useNavigate();
    const { logout } = useAuth0Logout();
    const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
    const [showWarning, setShowWarning] = useState<boolean>(false);
    const [showDeleteWarning, setShowDeleteWarning] = useState<boolean>(false);
    const [showPublishWarning, setShowPublishWarning] = useState<boolean>(false);
    const [draggedItemIds, setDraggedItemIds] = useState<number[]>([]);
    const [dragOverItemId, setDragOverItemId] = useState<number | null>(null);
    const [isDropInProgress, setIsDropInProgress] = useState<boolean>(false);
    const [isDragModeEnabled, setIsDragModeEnabled] = useState<boolean>(false);
    const [clipboardItemIds, setClipboardItemIds] = useState<number[]>([]);
    const [isClipboardProcessing, setIsClipboardProcessing] = useState<boolean>(false);
    const {
        listDetails,
        items,
        parentListId,
        loading,
        error,
        showSuccessIndicator,
        deleteSuccessMessage,
        isDeleting,
        isPublishing,
        showAddContentModal,
        openAddContentModal,
        closeAddContentModal,
        showEditContentModal,
        closeEditContentModal,
        isFetchingEditItem,
        itemToEdit,
        setError,
        fetchContent,
        handleAddContent,
        prepareItemForEdit,
        handleEditContent,
        deleteItems,
        togglePublishStatus,
        getParentToggleState,
        getSelectedItems,
        triggerSuccessIndicator,
    } = useContentManagerData(listId);

    // Check authentication on mount
    useEffect(() => {
        if (!isAuthenticated()) {
            void navigate("/access-content-manager");
        }
    }, [navigate]);

    useEffect(() => {
        setSelectedItemIds((prevSelectedIds) =>
            prevSelectedIds.filter((id) => items.some((item) => item.id === id)),
        );
    }, [items]);

    const handleItemClick = (itemId: number, allowMultiSelect: boolean) => {
        setSelectedItemIds((prevSelectedIds) => {
            const alreadySelected = prevSelectedIds.includes(itemId);

            if (allowMultiSelect) {
                return alreadySelected
                    ? prevSelectedIds.filter((id) => id !== itemId)
                    : [...prevSelectedIds, itemId];
            }

            if (isDragModeEnabled && alreadySelected && prevSelectedIds.length > 1) {
                return prevSelectedIds;
            }

            return alreadySelected && prevSelectedIds.length === 1 ? [] : [itemId];
        });
    };

    const handleSelectionDotKeyDown = (
        event: React.KeyboardEvent<HTMLDivElement>,
        itemId: number,
    ) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleItemClick(itemId, true);
        }
    };

    const handleItemDoubleClick = (item: CurationItem) => {
        // Block navigation only for assets (which cannot have children)
        // Smart lists are navigable to view their dynamically fetched assets
        if (!item.listId) {
            return;
        }

        const contentType = item.contentType?.toLowerCase();
        const isAsset = contentType === "asset";

        if (!isAsset) {
            void navigate(`/content-manager/${item.listId}`);
        }
    };

    const isDroppableItem = (item: CurationItem | null | undefined) => {
        if (!item?.listId) {
            return false;
        }
        const contentType = item.contentType?.toLowerCase();
        return contentType === "menu" || contentType === "list";
    };

    useEffect(() => {
        if (!isDragModeEnabled) {
            setDraggedItemIds([]);
            setDragOverItemId(null);
            setIsDropInProgress(false);
        }
    }, [isDragModeEnabled]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && !event.repeat) {
                const target = event.target as HTMLElement | null;
                if (target) {
                    const tagName = target.tagName;
                    if (
                        target.isContentEditable ||
                        tagName === "INPUT" ||
                        tagName === "TEXTAREA" ||
                        tagName === "SELECT"
                    ) {
                        return;
                    }
                }

                const key = event.key.toLowerCase();

                if (key === "c") {
                    if (selectedItemIds.length > 0) {
                        event.preventDefault();
                        setClipboardItemIds([...selectedItemIds]);
                    }
                }

                if (key === "v") {
                    if (clipboardItemIds.length === 0 || !listId || isClipboardProcessing) {
                        return;
                    }

                    event.preventDefault();

                    const performPaste = async () => {
                        setIsClipboardProcessing(true);
                        try {
                            for (const sourceId of clipboardItemIds) {
                                await copyListItem(sourceId, listId, {
                                    index: 0,
                                });
                            }
                            await fetchContent();
                            triggerSuccessIndicator();
                        } catch (e: unknown) {
                            const error = e as Error;
                            setError(error.message ?? "Failed to paste items.");
                        } finally {
                            setIsClipboardProcessing(false);
                        }
                    };

                    void performPaste();
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [
        clipboardItemIds,
        fetchContent,
        isClipboardProcessing,
        listId,
        selectedItemIds,
        setError,
        triggerSuccessIndicator,
    ]);

    const handleWebsiteCreationComplete = useCallback(async () => {
        await fetchContent();
        triggerSuccessIndicator();
    }, [fetchContent, triggerSuccessIndicator]);

    const handleDragStart = (event: React.DragEvent<HTMLDivElement>, item: CurationItem) => {
        if (!isDragModeEnabled) {
            event.preventDefault();
            return;
        }
        const idsToDrag =
            selectedItemIds.includes(item.id) && selectedItemIds.length > 0
                ? selectedItemIds
                : [item.id];

        setDraggedItemIds(idsToDrag);
        if (!selectedItemIds.includes(item.id) || selectedItemIds.length === 0) {
            setSelectedItemIds(idsToDrag);
        }

        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("application/json", JSON.stringify({ itemIds: idsToDrag }));
    };

    const handleDragEnd = () => {
        setDraggedItemIds([]);
        setDragOverItemId(null);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>, targetItem: CurationItem) => {
        if (!isDragModeEnabled || !isDroppableItem(targetItem) || isDropInProgress) {
            return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        if (dragOverItemId !== targetItem.id) {
            setDragOverItemId(targetItem.id);
        }
    };

    const handleDragLeave = (targetItem: CurationItem) => {
        if (dragOverItemId === targetItem.id) {
            setDragOverItemId(null);
        }
    };

    const handleDrop = async (event: React.DragEvent<HTMLDivElement>, targetItem: CurationItem) => {
        if (!isDragModeEnabled || !isDroppableItem(targetItem) || isDropInProgress) {
            return;
        }
        event.preventDefault();
        setDragOverItemId(null);

        if (!listId) {
            setError("Unable to determine the current list.");
            return;
        }

        const idsFromDrag = draggedItemIds.length > 0 ? draggedItemIds : selectedItemIds;
        if (!idsFromDrag || idsFromDrag.length === 0) {
            return;
        }

        const sourceItems = idsFromDrag
            .map((sourceId) => items.find((item) => item.id === sourceId))
            .filter((item): item is CurationItem => Boolean(item));

        if (sourceItems.length === 0) {
            return;
        }

        const itemsEligibleForMove = sourceItems.filter(
            (source) => source.listId !== targetItem.listId,
        );

        if (itemsEligibleForMove.length === 0) {
            return;
        }

        setIsDropInProgress(true);

        try {
            for (const source of itemsEligibleForMove) {
                await copyListItem(source.id, targetItem.listId, {
                    index: 0,
                });
            }

            for (const source of itemsEligibleForMove) {
                await deleteListItem(source.id);
            }

            await fetchContent();
            triggerSuccessIndicator();
        } catch (e: unknown) {
            const error = e as Error;
            setError(error.message ?? "Failed to move items.");
        } finally {
            setIsDropInProgress(false);
            setDraggedItemIds([]);
        }
    };

    const handleUpClick = () => {
        if (parentListId) {
            void navigate(`/content-manager/${parentListId}`);
        }
    };

    const handleStartOverClick = () => {
        setShowWarning(true);
    };

    const confirmQuit = async () => {
        await logout();
    };

    const cancelQuit = () => {
        setShowWarning(false);
    };

    const handleDeleteClick = () => {
        if (selectedItemIds.length > 0) {
            setShowDeleteWarning(true);
        }
    };

    const handleDragModeToggle = useCallback(() => {
        setIsDragModeEnabled((prev) => !prev);
    }, []);

    const selectedItems = getSelectedItems(selectedItemIds);
    const hasValidSelection = selectedItems.length > 0;
    const singleSelectedItem = selectedItems.length === 1 ? selectedItems[0] : null;
    const isAssetSelected = (singleSelectedItem?.contentType ?? "").toLowerCase() === "asset";
    const isTogglePublished = hasValidSelection
        ? selectedItems.every((item) => item.published)
        : getParentToggleState();

    const toggleLabel = isTogglePublished ? "Unpublish" : "Publish";
    const isSmartList = listDetails?.contentType?.toLowerCase() === "list_smart";
    const editButtonTitle = (() => {
        if (isSmartList) {
            return "Cannot edit smart list items";
        }
        if (isAssetSelected) {
            return "Assets are read-only. Manage them in Vimond CMS.";
        }
        return "Edit";
    })();
    const isEditButtonDisabled =
        isSmartList || selectedItemIds.length !== 1 || isFetchingEditItem || isAssetSelected;

    const isPublishToggleDisabledForSmartList = isSmartList;
    const isPublishToggleDisabledForEditorialRoot =
        listId === "editorial_root" && !hasValidSelection;
    const isPublishToggleDisabled =
        isPublishToggleDisabledForSmartList || isPublishToggleDisabledForEditorialRoot;
    const publishToggleTitle = (() => {
        if (isPublishToggleDisabledForSmartList) {
            return "Cannot publish/unpublish smart list items";
        }
        if (isPublishToggleDisabledForEditorialRoot) {
            return "Select items to publish/unpublish";
        }
        return toggleLabel;
    })();

    const hasFullyQualifiedPath = !!(listDetails?.path && listDetails.path.length > 0);

    let breadcrumbItems: PathItem[];
    if (hasFullyQualifiedPath && listDetails.path) {
        breadcrumbItems = listDetails.path;
    } else if (listId) {
        breadcrumbItems = [
            {
                id: -1,
                listId: listId,
                title: listDetails?.title ?? listId,
            },
        ];
    } else {
        breadcrumbItems = [];
    }

    const handleBreadcrumbNavigate = (pathItem: PathItem, index: number) => {
        if (!hasFullyQualifiedPath || index === breadcrumbItems.length - 1) {
            return;
        }
        void navigate(`/content-manager/${pathItem.listId}`);
    };

    const showDropzone =
        isDragModeEnabled && (selectedItemIds.length > 0 || draggedItemIds.length > 0);
    const currentListTitle =
        listDetails?.title && listDetails.title.trim().length > 0
            ? listDetails.title
            : listId ?? "Content";

    // Drag mode toggle labels
    let dragModeLabel: string;
    if (isSmartList) {
        dragModeLabel = "Cannot drag items in smart list";
    } else if (isDragModeEnabled) {
        dragModeLabel = "Disable drag mode";
    } else {
        dragModeLabel = "Enable drag mode";
    }

    const publishActionLabel = isTogglePublished ? "Unpublish" : "Publish";
    const publishTargetLabel =
        selectedItems.length > 0
            ? `${selectedItems.length} selected item${selectedItems.length > 1 ? "s" : ""}`
            : currentListTitle;

    const handlePublishToggleClick = () => {
        if (isPublishToggleDisabled) {
            return;
        }
        setShowPublishWarning(true);
    };

    const renderProgressOverlay = (
        isActive: boolean,
        overlayClass: string,
        dialogClass: string,
        title: string,
        message: string,
    ) => {
        if (!isActive) {
            return null;
        }
        return (
            <div
                className={`warning-overlay ${overlayClass}`}
                role="alertdialog"
                aria-live="assertive"
            >
                <div className={`warning-dialog ${dialogClass}`}>
                    <div className="loading-spinner" aria-hidden="true">
                        <svg className="spinner-icon" viewBox="0 0 50 50">
                            <circle
                                className="spinner-path"
                                cx="25"
                                cy="25"
                                r="20"
                                fill="none"
                                strokeWidth="5"
                            />
                        </svg>
                    </div>
                    <h2>{title}</h2>
                    <p>{message}</p>
                </div>
            </div>
        );
    };

    const childrenGrid = (
        <ContentChildrenGrid
            items={items}
            selectedItemIds={selectedItemIds}
            draggedItemIds={draggedItemIds}
            dragOverItemId={dragOverItemId}
            onItemClick={handleItemClick}
            onItemDoubleClick={handleItemDoubleClick}
            onSelectionDotKeyDown={handleSelectionDotKeyDown}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            isDroppableItem={isDroppableItem}
            dragEnabled={isDragModeEnabled}
            isSmartList={isSmartList}
        />
    );

    return (
        <div className="content-manager-container">
            <div className="content-manager-layout">
                <section className="content-manager-main">
                    <div className="header-container">
                        <h1>CURATION</h1>
                        {showSuccessIndicator && <div className="save-indicator">✓ Saved</div>}
                        {deleteSuccessMessage && (
                            <div className="delete-success-indicator">{deleteSuccessMessage}</div>
                        )}
                    </div>
                    <p>
                        Managing content for list ID: <strong>{listId}</strong>
                    </p>

                    <ContentManagerBreadcrumbs
                        items={breadcrumbItems}
                        hasFullPath={hasFullyQualifiedPath}
                        onNavigate={handleBreadcrumbNavigate}
                    />

                    <div className="toolbar-wrapper">
                        <div className="actions-toolbar">
                            <button
                                className="action-button add-button"
                                title={isSmartList ? "Cannot add to smart list" : "Add"}
                                onClick={openAddContentModal}
                                disabled={isSmartList}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    width="24px"
                                    height="24px"
                                >
                                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                                </svg>
                            </button>
                            <button
                                className="action-button edit-button"
                                title={editButtonTitle}
                                onClick={async () => {
                                    const item = await prepareItemForEdit(selectedItemIds);
                                    if (item) {
                                        setSelectedItemIds([item.id]);
                                    }
                                }}
                                disabled={isEditButtonDisabled}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    width="24px"
                                    height="24px"
                                >
                                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                                </svg>
                            </button>
                            <button
                                className="action-button delete-button"
                                title={isSmartList ? "Cannot delete from smart list" : "Delete"}
                                onClick={handleDeleteClick}
                                disabled={isSmartList || selectedItemIds.length === 0}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    width="24px"
                                    height="24px"
                                >
                                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                </svg>
                            </button>
                            <div
                                className={`drag-toggle-container ${isDragModeEnabled ? "active" : ""} ${isSmartList ? "disabled" : ""}`}
                                title={dragModeLabel}
                                onClick={isSmartList ? undefined : handleDragModeToggle}
                                role="button"
                                tabIndex={isSmartList ? -1 : 0}
                                aria-pressed={isSmartList ? undefined : isDragModeEnabled}
                                aria-disabled={isSmartList}
                                aria-label={dragModeLabel}
                                onKeyDown={(event) => {
                                    if (
                                        !isSmartList &&
                                        (event.key === "Enter" || event.key === " ")
                                    ) {
                                        event.preventDefault();
                                        handleDragModeToggle();
                                    }
                                }}
                            >
                                <svg
                                    className="publish-icon"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    width="24px"
                                    height="24px"
                                >
                                    <path d="M3 9l4-4 1.41 1.41L6.83 8H11v2H6.83l1.58 1.59L7 13l-4-4zm18 4l-4 4-1.41-1.41L17.17 16H13v-2h4.17l-1.58-1.59L17 11l4 4z" />
                                </svg>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={isDragModeEnabled}
                                        readOnly
                                        onClick={(event) => event.stopPropagation()}
                                    />
                                    <span className="slider" />
                                </label>
                            </div>
                            <div
                                className={`publish-toggle-container ${isPublishToggleDisabled ? "disabled" : ""}`}
                                role="button"
                                tabIndex={0}
                                aria-disabled={isPublishToggleDisabled}
                                aria-pressed={
                                    !isPublishToggleDisabled ? isTogglePublished : undefined
                                }
                                onClick={handlePublishToggleClick}
                                onKeyDown={(event) => {
                                    if (
                                        (event.key === "Enter" ||
                                            event.key === " " ||
                                            event.key === "Spacebar") &&
                                        !isPublishToggleDisabled
                                    ) {
                                        event.preventDefault();
                                        handlePublishToggleClick();
                                    }
                                }}
                                title={publishToggleTitle}
                            >
                                <BaseActionIcon className="publish-icon">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
                                    <circle cx="12" cy="12" r="5" />
                                </BaseActionIcon>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={isTogglePublished}
                                        readOnly
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <span className="slider" />
                                </label>
                            </div>
                            <button
                                onClick={handleStartOverClick}
                                className="action-button quit-button"
                                title="Quit"
                            >
                                <BaseActionIcon>
                                    <path d="M10.09 15.59L11.5 17l5-5-5-5-1.41 1.41L12.67 11H3v2h9.67l-2.58 2.59zM19 3H5c-1.11 0-2 .9-2 2v4h2V5h14v14H5v-4H3v4c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
                                </BaseActionIcon>
                            </button>
                        </div>
                    </div>

                    {loading && <div className="loading-indicator">Loading content...</div>}

                    {error && (
                        <div className="error-message">
                            <strong className="error-title">An Error Occurred</strong>
                            <pre className="error-content">{error}</pre>
                        </div>
                    )}

                    {showAddContentModal && (
                        <AddContentModal
                            onClose={closeAddContentModal}
                            onAddContent={handleAddContent}
                            parentType={listDetails?.contentType}
                            onWebsiteStructureCreated={handleWebsiteCreationComplete}
                        />
                    )}

                    {showEditContentModal && itemToEdit && (
                        <EditContentModal
                            onClose={closeEditContentModal}
                            onEditContent={handleEditContent}
                            item={itemToEdit}
                            showSuccessIndicator={showSuccessIndicator}
                        />
                    )}

                    {!loading && !error && (
                        <div className="children-section">
                            <div className="children-header">
                                <button
                                    className="up-button"
                                    onClick={handleUpClick}
                                    disabled={!parentListId}
                                    aria-label="Navigate to parent list"
                                    title="Up"
                                />
                                <h3>{currentListTitle}</h3>
                            </div>
                            {showDropzone ? (
                                <div
                                    className={`children-dropzone ${draggedItemIds.length > 0 ? "drag-active" : ""}`}
                                    role="region"
                                    aria-label={`Content drop window for ${currentListTitle}`}
                                >
                                    <div className="dropzone-instructions">
                                        {isDropInProgress
                                            ? "Moving items..."
                                            : "Drag items onto a list or menu to move them."}
                                    </div>
                                    {childrenGrid}
                                </div>
                            ) : (
                                childrenGrid
                            )}
                        </div>
                    )}

                    {showWarning && (
                        <div className="warning-overlay">
                            <div className="warning-dialog">
                                <h2>Are you sure?</h2>
                                <p>
                                    This will clear your session and redirect you to the Content
                                    Manager access page.
                                </p>
                                <div className="warning-buttons">
                                    <button onClick={cancelQuit} className="cancel-button">
                                        Cancel
                                    </button>
                                    <button onClick={confirmQuit} className="confirm-button">
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showDeleteWarning && (
                        <div
                            className="warning-overlay delete-overlay"
                            role="dialog"
                            aria-modal="true"
                        >
                            <div className="warning-dialog delete-dialog">
                                <h2>Delete Items?</h2>
                                <p>
                                    Are you sure you want to delete {selectedItemIds.length}{" "}
                                    selected item(s)? This action cannot be undone.
                                </p>
                                <div className="warning-buttons">
                                    <button
                                        onClick={() => setShowDeleteWarning(false)}
                                        className="cancel-button"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={async () => {
                                            setShowDeleteWarning(false);
                                            const deleted = await deleteItems(selectedItemIds);
                                            if (deleted) {
                                                setSelectedItemIds([]);
                                            }
                                        }}
                                        className="confirm-button"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showPublishWarning && (
                        <div
                            className="warning-overlay publish-overlay"
                            role="dialog"
                            aria-modal="true"
                        >
                            <div className="warning-dialog publish-dialog">
                                <h2>{publishActionLabel}?</h2>
                                <p>
                                    Are you sure you want to {publishActionLabel.toLowerCase()}{" "}
                                    {publishTargetLabel}?
                                </p>
                                <div className="warning-buttons">
                                    <button
                                        onClick={() => setShowPublishWarning(false)}
                                        className="cancel-button"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={async () => {
                                            setShowPublishWarning(false);
                                            await togglePublishStatus(selectedItemIds);
                                        }}
                                        className="confirm-button"
                                    >
                                        {publishActionLabel}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {renderProgressOverlay(
                        isDeleting,
                        "delete-overlay",
                        "delete-dialog delete-progress-dialog",
                        "Deleting items",
                        "Please wait while we remove the selected content…",
                    )}

                    {renderProgressOverlay(
                        isPublishing,
                        "publish-overlay",
                        "publish-dialog publish-progress-dialog",
                        isTogglePublished ? "Unpublishing items" : "Publishing items",
                        `Please wait while we ${isTogglePublished ? "unpublish" : "publish"} the selected content…`,
                    )}
                </section>
            </div>
        </div>
    );
};

export default ContentManager;
